import { LitElement, html, css, nothing } from 'lit';
// Registers <ol-button> for the header close control.
import './OLButton.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import './OlIcon.js';
import { findFocusableIndex, getDeepActiveElement, getTabbableFromSlot, isFocusable } from './utils/focus-utils.js';
import { lockBodyScroll, unlockBodyScroll } from './utils/scroll-lock.js';
import { slotHasContent } from './utils/slot-utils.js';

/**
 * A modal dialog built on the native `<dialog>` element. Provides focus trap,
 * focus restoration, scroll lock (via `showModal()`), backdrop dismissal, and
 * Escape-to-close out of the box. Supports slotted header/body/footer content
 * and a fullscreen mode for mobile.
 *
 * @element ol-dialog
 *
 * @prop {Boolean} open - Whether the dialog is open.
 * @prop {String} label - Title shown in the default header. Also used as the
 *     accessible name when `withoutHeader` is true.
 * @prop {String} icon - Name of an `ol-icon` to show before the title in the
 *     default header. Decorative: the title already names the dialog.
 * @prop {Boolean} withoutHeader - Hide the default header (title + close
 *     button). The `header` slot still works.
 * @prop {Boolean} alert - Announce as `role="alertdialog"`, with the body as
 *     the accessible description. For confirmations and alerts that interrupt
 *     the reader; see `olConfirm()` in alert-dialog.js.
 * @prop {String} labelClose - Translated accessible name for the default close
 *     button. Attribute: `label-close`. Default "Close dialog".
 * @prop {String} returnValue - Set by `close(returnValue)`; empty when closed by
 *     Escape, the backdrop, or the close button. Reset each time it opens.
 * @prop {'small' | 'medium' | 'large'} width - Width preset: `'small'` (400px),
 *     `'medium'` (550px, default), or `'large'` (800px). Override per-instance
 *     via `--ol-dialog-width-*` host CSS variables.
 * @prop {Boolean} closeOnBackdropClick - Whether clicking the backdrop closes
 *     the dialog. Default `true`. Attribute: `close-on-backdrop-click`.
 * @prop {Boolean} closeOnEscape - Whether pressing Escape closes the dialog.
 *     Default `true`. Attribute: `close-on-escape`.
 * @prop {Boolean} fullscreenOnMobile - At viewports ≤767px, render edge-to-edge
 *     (full viewport, no border-radius). Attribute: `fullscreen-on-mobile`.
 * @prop {'center' | 'top'} placement - `'center'` (default) keeps the dialog vertically
 *     centered like a normal modal. `'top'` anchors it a fixed distance from
 *     the top of the viewport so the top edge stays put as content grows or
 *     shrinks (command-palette / search-modal pattern).
 *
 * @slot - Default slot for the dialog body.
 * @slot header - Optional custom header. When filled, replaces the default
 *     title + close-button row entirely. Useful for search bars, custom
 *     toolbars, etc.
 * @slot footer - Slot for action buttons. Footer region is hidden when empty.
 *
 * @cssprop --ol-dialog-padding - Padding around body and footer regions.
 *     Set to `0` for edge-to-edge content (e.g. when slotting a search bar
 *     or filter row that owns its own padding).
 * @cssprop [--ol-dialog-max-height=85vh] - Maximum dialog height; the body scrolls past it.
 *     Slotted content can read it to size itself to fit (e.g. an enlarged image).
 * @cssprop --ol-dialog-border-radius - Corner radius (ignored in fullscreen mode).
 * @cssprop --ol-dialog-backdrop-color - Backdrop color.
 * @cssprop --ol-dialog-backdrop-blur - Blur radius applied to the page behind
 *     the backdrop. Set to `0` to dim without blurring.
 * @cssprop --ol-dialog-animation-duration - Open/close animation duration.
 * @cssprop --ol-dialog-top-offset - Distance from viewport top when
 *     `placement="top"`. Default `clamp(40px, 8vh, 96px)`.
 *
 * @fires ol-open - Fires when the dialog starts opening.
 * @fires ol-after-open - Fires after the open animation completes.
 * @fires ol-close - Fires when the dialog starts closing. Cancelable —
 *     calling `event.preventDefault()` keeps the dialog open.
 *     `detail.returnValue` says how it was closed.
 * @fires ol-after-close - Fires after the close animation completes, with the
 *     same `detail.returnValue`.
 *
 * @example
 * <ol-dialog label="Edit profile" width="medium" open>
 *   <p>Form goes here.</p>
 *   <button slot="footer">Save</button>
 * </ol-dialog>
 *
 * @example
 * <!-- Read which button closed it, like native <dialog> -->
 * <ol-dialog label="Discard draft?" width="small" alert>
 *   <p>Your changes will be lost.</p>
 *   <ol-button slot="footer" onclick="this.closest('ol-dialog').close('discard')">Discard</ol-button>
 * </ol-dialog>
 * dialog.addEventListener('ol-after-close', (e) => e.detail.returnValue === 'discard');
 *
 * @example
 * <!-- Custom header (e.g. a search bar) with no body padding -->
 * <ol-dialog without-header fullscreen-on-mobile
 *            style="--ol-dialog-padding: 0">
 *   <div slot="header"><input type="search" placeholder="Search…"/></div>
 *   <div>Results…</div>
 * </ol-dialog>
 */
export class OlDialog extends LitElement {
    static properties = {
        open: { type: Boolean, reflect: true },
        label: { type: String },
        icon: { type: String },
        withoutHeader: { type: Boolean, attribute: 'without-header' },
        alert: { type: Boolean, reflect: true },
        labelClose: { type: String, attribute: 'label-close' },
        // Reflected: the width presets are :host([width=…]) rules, so setting the
        // property in JS (as olConfirm() does) has to reach the attribute.
        width: { type: String, reflect: true },
        closeOnBackdropClick: { type: Boolean, attribute: 'close-on-backdrop-click' },
        closeOnEscape: { type: Boolean, attribute: 'close-on-escape' },
        fullscreenOnMobile: { type: Boolean, attribute: 'fullscreen-on-mobile', reflect: true },
        placement: { type: String, reflect: true },
        _hasHeaderContent: { state: true },
        _hasFooterContent: { state: true }
    };

    static styles = css`
        :host {
            --ol-dialog-width-small: 400px;
            --ol-dialog-width-medium: 550px;
            --ol-dialog-width-large: 800px;
            --ol-dialog-padding: var(--spacing-xl);
            --ol-dialog-max-height: 85vh;
            --ol-dialog-border-radius: var(--border-radius-overlay);
            --ol-dialog-animation-duration: var(--duration-base);
            --ol-dialog-backdrop-color: var(--overlay-backdrop-color);
            --ol-dialog-backdrop-blur: var(--overlay-backdrop-blur);
            --ol-dialog-top-offset: clamp(40px, 8vh, 96px);

            font-family: var(--font-family-body);
        }

        dialog {
            border: var(--border-overlay);
            border-radius: var(--ol-dialog-border-radius);
            padding: 0;
            max-width: 90vw;
            max-height: var(--ol-dialog-max-height);
            overflow: hidden;
            box-shadow: var(--box-shadow-overlay);
        }

        dialog:focus {
            outline: none;
        }

        dialog:focus-visible {
            outline: var(--focus-width) solid var(--color-focus-ring);
            outline-offset: 2px;
        }

        dialog[open] {
            display: flex;
            flex-direction: column;
            animation: dialog-open var(--ol-dialog-animation-duration) var(--ease-enter);
        }

        dialog.closing {
            animation: dialog-close var(--ol-dialog-animation-duration) var(--ease-exit);
        }

        /* The blur does the separating, so the dim can stay light — enough to
           mute the page without darkening the room. Both fade together: the
           keyframes animate opacity, which carries the filter with it. */
        dialog::backdrop {
            background-color: var(--ol-dialog-backdrop-color);
            backdrop-filter: blur(var(--ol-dialog-backdrop-blur));
            -webkit-backdrop-filter: blur(var(--ol-dialog-backdrop-blur));
            animation: backdrop-fade-in var(--ol-dialog-animation-duration) var(--ease-enter);
        }

        dialog.closing::backdrop {
            animation: backdrop-fade-out var(--ol-dialog-animation-duration) var(--ease-exit);
        }

        @keyframes dialog-open {
            from {
                opacity: 0;
                transform: scale(0.95);
            }
            to {
                opacity: 1;
                transform: scale(1);
            }
        }

        @keyframes dialog-close {
            from {
                opacity: 1;
                transform: scale(1);
            }
            to {
                opacity: 0;
                transform: scale(0.95);
            }
        }

        @keyframes backdrop-fade-in {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }

        @keyframes backdrop-fade-out {
            from {
                opacity: 1;
            }
            to {
                opacity: 0;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            dialog[open],
            dialog.closing,
            dialog::backdrop,
            dialog.closing::backdrop,
            :host([placement='top']) dialog[open],
            :host([placement='top']) dialog.closing {
                animation: none;
            }
        }

        /* Top-anchored placement: keeps the dialog's top edge fixed as its
           own height grows or shrinks (search modal / command palette). */
        :host([placement='top']) {
            --ol-dialog-max-height: calc(100dvh - var(--ol-dialog-top-offset) - var(--spacing-xl));
        }

        :host([placement='top']) dialog {
            margin-block-start: var(--ol-dialog-top-offset);
            margin-block-end: auto;
            transform-origin: top center;
        }

        :host([placement='top']) dialog[open] {
            animation: dialog-open-top var(--ol-dialog-animation-duration) var(--ease-enter);
        }

        :host([placement='top']) dialog.closing {
            animation: dialog-close-top var(--ol-dialog-animation-duration) var(--ease-exit);
        }

        @keyframes dialog-open-top {
            from {
                opacity: 0;
                transform: translateY(-8px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        @keyframes dialog-close-top {
            from {
                opacity: 1;
                transform: translateY(0);
            }
            to {
                opacity: 0;
                transform: translateY(-8px);
            }
        }

        /* Width variants */
        :host([width='small']) dialog {
            width: var(--ol-dialog-width-small);
        }

        :host([width='medium']) dialog {
            width: var(--ol-dialog-width-medium);
        }

        :host([width='large']) dialog {
            width: var(--ol-dialog-width-large);
        }

        /* Fullscreen on mobile — overrides width preset and removes chrome.
           Also neutralizes placement="top" so the dialog truly fills the
           viewport (no top offset, no leftover max-height clamp). */
        @media (max-width: 767px) {
            :host([fullscreen-on-mobile]) {
                --ol-dialog-max-height: 100dvh;
            }

            :host([fullscreen-on-mobile]) dialog {
                width: 100vw;
                height: 100dvh;
                max-width: none;
                max-height: none;
                border: none;
                border-radius: 0;
            }

            :host([fullscreen-on-mobile][placement='top']) dialog {
                margin-block-start: 0;
                margin-block-end: 0;
            }
        }

        .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: var(--ol-dialog-padding) var(--ol-dialog-padding) 0 var(--ol-dialog-padding);
        }

        .header.hidden {
            display: none;
        }

        h2.title {
            display: flex;
            align-items: center;
            gap: var(--spacing-sm);
            margin: 0;
            padding: 0;
            font-size: 1.25rem;
            font-weight: 600;
        }

        .body {
            padding: var(--ol-dialog-padding);
            overflow-y: auto;
            overscroll-behavior: contain;
        }

        .footer {
            padding: 0 var(--ol-dialog-padding) var(--ol-dialog-padding) var(--ol-dialog-padding);
        }

        .footer[hidden] {
            display: none;
        }
    `;

    constructor() {
        super();
        this.open = false;
        this.label = '';
        this.icon = '';
        this.withoutHeader = false;
        this.alert = false;
        this.labelClose = 'Close dialog';
        this.returnValue = '';
        this.width = 'medium';
        this.closeOnBackdropClick = true;
        this.closeOnEscape = true;
        this.fullscreenOnMobile = false;
        this.placement = 'center';
        this._hasHeaderContent = false;
        this._hasFooterContent = false;

        /** @type {HTMLElement|null} Element that had focus before dialog opened */
        this._previouslyFocusedElement = null;

        /** @type {boolean} Whether this dialog currently holds a body scroll lock */
        this._scrollLocked = false;

        /** @type {boolean} Whether the dismiss-keyboard-on-scroll listener is attached */
        this._blurOnScrollActive = false;
        this._handleTouchMoveBlur = this._handleTouchMoveBlur.bind(this);

        this._handleCancel = this._handleCancel.bind(this);
        this._handleBackdropClick = this._handleBackdropClick.bind(this);
        this._handleHeaderSlotChange = this._handleHeaderSlotChange.bind(this);
        this._handleFooterSlotChange = this._handleFooterSlotChange.bind(this);
        this._handleKeyDown = this._handleKeyDown.bind(this);
    }

    /** Unique ID for ARIA labelledby association */
    get _titleId() {
        return `${this.id || 'ol-dialog'}-title`;
    }

    get _bodyId() {
        return `${this.id || 'ol-dialog'}-body`;
    }

    /** @returns {HTMLDialogElement} */
    get dialog() {
        return this.renderRoot?.querySelector('dialog');
    }

    /**
     * Closes the dialog, recording why — mirrors native `HTMLDialogElement.close()`.
     * @param {String} [returnValue]
     */
    close(returnValue) {
        if (returnValue !== undefined) this.returnValue = String(returnValue);
        this.open = false;
    }

    updated(changedProperties) {
        if (changedProperties.has('open')) {
            if (this.open) {
                this._openDialog();
            } else if (changedProperties.get('open') === true) {
                this._closeDialog();
            }
        }
    }

    _openDialog() {
        const dialog = this.dialog;
        if (!dialog || dialog.open) return;

        this.returnValue = '';

        // document.activeElement doesn't pass into shadow DOM
        this._previouslyFocusedElement = getDeepActiveElement();

        this.dispatchEvent(
            new CustomEvent('ol-open', {
                bubbles: true,
                composed: true
            })
        );

        dialog.showModal();

        // Native showModal() locks scroll on desktop but NOT background touch-scroll
        // on iOS Safari, so on mobile we pin <body> ourselves. On desktop the native
        // lock suffices and skipping this avoids a scrollbar-width layout shift.
        if (window.matchMedia('(max-width: 767px)').matches) {
            lockBodyScroll();
            this._scrollLocked = true;
            this._attachBlurOnScroll();
        }

        // Capture phase to intercept Tab before Safari's native handling.
        document.addEventListener('keydown', this._handleKeyDown, true);

        this._setInitialFocus();

        this._afterAnimation(dialog, () => {
            this.dispatchEvent(
                new CustomEvent('ol-after-open', {
                    bubbles: true,
                    composed: true
                })
            );
        });
    }

    /**
     * Sets initial focus when dialog opens.
     * Priority: [autofocus] > first focusable in body > close button > dialog
     *
     * Deferred a frame on purpose: focusing this late spends the tap's transient
     * activation, so on mobile the field takes the caret without the soft
     * keyboard coming up over the dialog.
     */
    _setInitialFocus() {
        requestAnimationFrame(() => {
            const dialog = this.dialog;
            if (!dialog) return;

            const autofocusEl = this.querySelector('[autofocus]');
            if (autofocusEl) {
                autofocusEl.focus();
                return;
            }

            const closeButton = this.renderRoot?.querySelector('.close-button');

            // Land initial focus on the same element the Tab trap treats as the
            // first stop, using its shadow-piercing list so a slotted custom
            // element whose real focusable lives in its shadow root (e.g.
            // <ol-toggle>) is found — a light-DOM querySelector(FOCUSABLE_SELECTOR)
            // would miss it and strand focus elsewhere. Skip the synthetic close
            // button so focus still prefers real content over the X; we fall back
            // to it below only when there's nothing else to focus.
            const firstFocusable = this._getFocusableElements().find(el => el !== closeButton);
            if (firstFocusable) {
                firstFocusable.focus();
                return;
            }

            if (closeButton && !this.withoutHeader && !this._hasHeaderContent) {
                closeButton.focus();
                return;
            }

            dialog.focus();
        });
    }

    _closeDialog() {
        const dialog = this.dialog;
        if (!dialog || !dialog.open) return;

        const closeEvent = new CustomEvent('ol-close', {
            bubbles: true,
            composed: true,
            cancelable: true,
            detail: { returnValue: this.returnValue }
        });

        this.dispatchEvent(closeEvent);

        if (closeEvent.defaultPrevented) {
            this.returnValue = '';
            this.open = true;
            return;
        }

        document.removeEventListener('keydown', this._handleKeyDown, true);

        dialog.classList.add('closing');

        this._afterAnimation(dialog, () => {
            dialog.classList.remove('closing');
            dialog.close();

            this._detachBlurOnScroll();
            this._releaseScrollLock();
            this._restoreFocus();

            this.dispatchEvent(
                new CustomEvent('ol-after-close', {
                    bubbles: true,
                    composed: true,
                    detail: { returnValue: this.returnValue }
                })
            );
        });
    }

    /**
     * Runs `done` once the dialog's current open/close animation finishes.
     *
     * The open/close transitions are CSS animations, so the obvious hook is the
     * `animationend` event. But under `prefers-reduced-motion: reduce` the
     * stylesheet sets `animation: none`, and an element with no animation never
     * dispatches `animationend` — so a naive listener would leave the dialog
     * stuck open (never calling `dialog.close()`, never restoring focus). We
     * read the resolved animation duration instead: 0 (reduced motion, or no
     * animation) runs `done` immediately, otherwise we wait on `animationend`
     * with a timer fallback in case the event is dropped (e.g. backgrounded tab).
     *
     * @param {HTMLDialogElement} dialog
     * @param {() => void} done
     */
    _afterAnimation(dialog, done) {
        const durationMs = this._animationDurationMs(dialog);
        if (durationMs <= 0) {
            done();
            return;
        }

        let finished = false;
        const finish = () => {
            if (finished) return;
            finished = true;
            clearTimeout(timer);
            dialog.removeEventListener('animationend', finish);
            done();
        };
        const timer = setTimeout(finish, durationMs + 50);
        dialog.addEventListener('animationend', finish);
    }

    /**
     * The dialog's resolved animation-duration in milliseconds (first value of
     * the list). Returns 0 when no animation is set (e.g. reduced motion).
     * @param {HTMLDialogElement} dialog
     * @returns {Number}
     */
    _animationDurationMs(dialog) {
        const raw = getComputedStyle(dialog).animationDuration.split(',')[0].trim();
        if (raw.endsWith('ms')) return parseFloat(raw) || 0;
        if (raw.endsWith('s')) return (parseFloat(raw) || 0) * 1000;
        return 0;
    }

    _restoreFocus() {
        if (this._previouslyFocusedElement && typeof this._previouslyFocusedElement.focus === 'function') {
            // setTimeout ensures focus happens after dialog is fully closed.
            setTimeout(() => {
                this._previouslyFocusedElement?.focus();
                this._previouslyFocusedElement = null;
            }, 0);
        }
    }

    /** Native dialog cancel event (Escape key). */
    _handleCancel(event) {
        // Prevent default close so we can animate.
        event.preventDefault();

        if (this.closeOnEscape) {
            this.open = false;
        }
    }

    _handleBackdropClick(event) {
        if (!this.closeOnBackdropClick) return;

        // Clicks on the ::backdrop register the dialog as the target.
        // Clicks on dialog content target a child element.
        if (event.target === this.dialog) {
            this.open = false;
        }
    }

    _handleCloseClick() {
        this.open = false;
    }

    /**
     * Returns all focusable elements within the dialog in DOM order:
     * header → body → footer. Includes the default close button when no
     * custom header is slotted.
     * @returns {HTMLElement[]}
     */
    _getFocusableElements() {
        if (!this.dialog) return [];

        const focusable = [];

        const headerSlot = this.renderRoot?.querySelector('slot[name="header"]');
        const headerSlotted = getTabbableFromSlot(headerSlot);
        if (headerSlotted.length > 0) {
            focusable.push(...headerSlotted);
        } else {
            const closeButton = this.renderRoot?.querySelector('.close-button');
            if (closeButton && !this.withoutHeader && isFocusable(closeButton)) {
                focusable.push(closeButton);
            }
        }

        const bodySlot = this.renderRoot?.querySelector('slot:not([name])');
        focusable.push(...getTabbableFromSlot(bodySlot));

        const footerSlot = this.renderRoot?.querySelector('slot[name="footer"]');
        focusable.push(...getTabbableFromSlot(footerSlot));

        return focusable;
    }

    /**
     * Walks up from `el` (across shadow boundaries) looking for an open
     * nested overlay inside this dialog — currently any `<ol-popover>`
     * with the `open` attribute set. Used to skip Tab trapping when a
     * sub-overlay is driving its own focus.
     * @param {Element|null} el
     * @returns {Boolean}
     */
    _isInsideOpenOverlay(el) {
        let cur = el;
        while (cur && cur !== this.dialog) {
            // ol-popover reflects `open` to its attribute (see OlPopover.js).
            // We match by tagName to avoid an import-time coupling to the
            // OlPopover class itself.
            if (cur.tagName === 'OL-POPOVER' && cur.hasAttribute('open')) {
                return true;
            }
            const parent = cur.parentNode;
            cur = parent?.nodeType === Node.DOCUMENT_FRAGMENT_NODE && parent.host ? parent.host : cur.parentElement;
        }
        return false;
    }

    /**
     * Whether `el` sits inside a different open <ol-dialog>, climbing shadow
     * boundaries. Nesting in the DOM counts too, so the innermost dialog wins.
     * @param {Element|null} el
     * @returns {Boolean}
     */
    _isInsideOtherDialog(el) {
        let cur = el;
        while (cur && cur !== this) {
            if (cur.tagName === 'OL-DIALOG' && cur.open) return true;
            const parent = cur.parentNode;
            cur = parent?.nodeType === Node.DOCUMENT_FRAGMENT_NODE && parent.host ? parent.host : cur.parentElement;
        }
        return false;
    }

    /**
     * Manual Tab focus trap. Needed because Safari doesn't trap focus across
     * shadow DOM boundaries for slotted content.
     */
    _handleKeyDown(event) {
        if (event.key !== 'Tab') return;

        const activeElement = getDeepActiveElement();

        // If focus is inside an open nested overlay (e.g. an <ol-popover> the
        // user just opened from a trigger in this dialog), that overlay owns
        // its own focus trap — don't intercept Tab or we'll yank focus back
        // out of the popover.
        if (this._isInsideOpenOverlay(activeElement)) return;

        // A dialog stacked on top of this one (e.g. olConfirm() opened from it)
        // traps its own focus; trapping here too would pull focus back beneath it.
        if (this._isInsideOtherDialog(activeElement)) return;

        const focusable = this._getFocusableElements();
        if (focusable.length === 0) return;

        event.preventDefault();

        // findFocusableIndex climbs shadow boundaries so a custom-element
        // wrapper (e.g. <ol-options-popover>) that delegates focus inward to
        // a deeper button still matches its host entry in the trap list.
        const currentIndex = findFocusableIndex(focusable, activeElement);

        let nextIndex;
        if (event.shiftKey) {
            nextIndex = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
        } else {
            nextIndex = currentIndex >= focusable.length - 1 ? 0 : currentIndex + 1;
        }

        focusable[nextIndex].focus();
    }

    _handleHeaderSlotChange(event) {
        this._hasHeaderContent = slotHasContent(event.target);
    }

    _handleFooterSlotChange(event) {
        this._hasFooterContent = slotHasContent(event.target);
    }

    connectedCallback() {
        super.connectedCallback();
        // firstUpdated() attaches these on first render, but it only ever runs
        // once. If the element is disconnected and reconnected, re-attach here
        // so Escape (cancel) and backdrop dismissal keep working. On the first
        // connect the dialog isn't rendered yet, so this no-ops and firstUpdated
        // does the work; addEventListener dedupes identical listeners, so any
        // overlap is harmless.
        this._attachDialogListeners();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('keydown', this._handleKeyDown, true);
        // Safety net: if the element is torn down while still open, don't leave the
        // body pinned.
        this._detachBlurOnScroll();
        this._releaseScrollLock();
        const dialog = this.dialog;
        if (dialog) {
            dialog.removeEventListener('cancel', this._handleCancel);
            dialog.removeEventListener('click', this._handleBackdropClick);
        }
    }

    /** Releases the body scroll lock if this dialog holds one. Idempotent. */
    _releaseScrollLock() {
        if (this._scrollLocked) {
            unlockBodyScroll();
            this._scrollLocked = false;
        }
    }

    /**
     * On a fullscreen mobile dialog, dismiss the on-screen keyboard as soon as the
     * user starts scrolling/dragging. The dialog is `100dvh`, which doesn't shrink
     * for the keyboard — so while an input is focused iOS lets the user pan the
     * visual viewport and expose the page behind the dialog. Blurring the input on
     * drag closes the keyboard, removing the taller-than-visible region (and the pan)
     * before it can happen. `touchmove` is the trigger rather than `scroll` because a
     * short result list doesn't scroll, yet still pans — and touchmove fires anyway.
     */
    _attachBlurOnScroll() {
        if (!this.fullscreenOnMobile) return;
        const dialog = this.dialog;
        if (!dialog) return;
        dialog.addEventListener('touchmove', this._handleTouchMoveBlur, { passive: true });
        this._blurOnScrollActive = true;
    }

    _handleTouchMoveBlur() {
        const el = getDeepActiveElement();
        if (el && (el.matches('input, textarea') || el.isContentEditable)) {
            el.blur();
        }
    }

    _detachBlurOnScroll() {
        if (!this._blurOnScrollActive) return;
        const dialog = this.dialog;
        if (dialog) dialog.removeEventListener('touchmove', this._handleTouchMoveBlur);
        this._blurOnScrollActive = false;
    }

    _attachDialogListeners() {
        const dialog = this.dialog;
        if (!dialog) return;
        dialog.addEventListener('cancel', this._handleCancel);
        dialog.addEventListener('click', this._handleBackdropClick);
    }

    firstUpdated() {
        this._attachDialogListeners();
        if (this.open) {
            this._openDialog();
        }
    }

    render() {
        const showDefaultHeader = !this.withoutHeader && !this._hasHeaderContent;
        const ariaLabel = (this.withoutHeader || this._hasHeaderContent) && this.label ? this.label : undefined;
        const ariaLabelledBy = showDefaultHeader && this.label ? this._titleId : undefined;

        return html`
            <dialog
                role=${this.alert ? 'alertdialog' : 'dialog'}
                aria-modal="true"
                aria-label=${ifDefined(ariaLabel)}
                aria-labelledby=${ifDefined(ariaLabelledBy)}
                aria-describedby=${ifDefined(this.alert ? this._bodyId : undefined)}
            >
                <header class="header ${showDefaultHeader ? '' : 'hidden'}">
                    <h2 class="title" id=${this._titleId}>${this.icon ? html`<ol-icon name=${this.icon}></ol-icon>` : nothing} ${this.label}</h2>
                    <ol-button class="close-button" shape="icon" variant="ghost" aria-label=${this.labelClose} @click=${this._handleCloseClick}>
                        <ol-icon name="x"></ol-icon>
                    </ol-button>
                </header>
                <slot name="header" @slotchange=${this._handleHeaderSlotChange}></slot>
                <div class="body" id=${this._bodyId}>
                    <slot></slot>
                </div>
                <footer class="footer" ?hidden=${!this._hasFooterContent}>
                    <slot name="footer" @slotchange=${this._handleFooterSlotChange}></slot>
                </footer>
            </dialog>
        `;
    }
}

if (!customElements.get('ol-dialog')) {
    customElements.define('ol-dialog', OlDialog);
}
